import { useCallback, useEffect, useState } from "react";
import { getMaterialSdsBinderPreview } from "@/lib/manufacturing/materialsMsdsClient";
import type { MfgSdsBinderCandidate } from "@/lib/manufacturing/msdsBinderTypes";
import { renderSdsBinderPdfToBlob } from "@/components/manufacturing/pdf/generateSdsBinderPdf";

type Args = {
  open: boolean;
  enabled: boolean;
  plantId?: string;
};

export function useManufacturingSdsBook(args: Args) {
  const { open, enabled, plantId } = args;
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plantName, setPlantName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [includesDrafts, setIncludesDrafts] = useState(false);
  const [candidates, setCandidates] = useState<MfgSdsBinderCandidate[]>([]);

  const reset = useCallback(() => {
    setLoading(false);
    setExporting(false);
    setError(null);
    setPlantName("");
    setOrganizationName("");
    setIncludesDrafts(false);
    setCandidates([]);
  }, []);

  useEffect(() => {
    if (!open || !enabled) {
      reset();
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const r = await getMaterialSdsBinderPreview(plantId);
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error || "Failed to load SDS book preview");
        setLoading(false);
        return;
      }
      setPlantName(r.data.plant_name);
      setOrganizationName(r.data.organization_name);
      setIncludesDrafts(r.data.includes_drafts);
      setCandidates(r.data.candidates);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, enabled, plantId, reset]);

  const download = useCallback(async () => {
    setExporting(true);
    setError(null);
    const result = await renderSdsBinderPdfToBlob({
      organizationName,
      plantName,
      exportedAt: new Date(),
      includesDrafts,
      sheets: candidates.map((c) => ({
        materialCode: c.material_code,
        materialName: c.material_name,
        category: c.category,
        statusChip: c.status_chip,
        sections: c.sections,
        footerState: c.footer_state,
        source: c.source,
        generatedAt: c.ai_generated_at,
        generatedByUserId: c.ai_generated_by_user_id,
        promptVersion: c.ai_prompt_version,
        sourceInputsHash: c.source_inputs_hash,
        reviewerDisplayName: c.review_signed_off_display_name,
        reviewedAt: c.review_signed_off_at,
        plantName,
        organizationName,
      })),
    });
    setExporting(false);
    if (!result.ok) {
      setError(result.error);
      return result;
    }
    const url = URL.createObjectURL(result.blob);
    const a = window.document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `SDS-book-${plantName || "plant"}-${stamp}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    return { ok: true as const };
  }, [candidates, includesDrafts, organizationName, plantName]);

  return {
    loading,
    exporting,
    error,
    plantName,
    organizationName,
    includesDrafts,
    candidates,
    download,
    reset,
  };
}
